<template>
  <AdminLayout>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="mb-6">
        <router-link to="/audit" class="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-3">
          <ArrowLeft class="w-4 h-4" /> Retour aux logs d'audit
        </router-link>
        <div class="flex items-center gap-3">
          <div class="p-2.5 bg-blue-50 rounded-xl">
            <User class="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 class="text-2xl sm:text-3xl font-bold text-dashboard-text">Activité — Utilisateur #{{ userId }}</h1>
            <p v-if="userName" class="text-sm text-dashboard-muted mt-0.5">{{ userName }}</p>
          </div>
        </div>
      </div>

      <!-- Filtres période -->
      <div class="bg-white border border-dashboard-border rounded-xl p-4 mb-5 flex items-center gap-3 flex-wrap">
        <span class="text-sm text-dashboard-muted">Période :</span>
        <input v-model="dateFrom" type="date" class="px-3 py-1.5 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm" />
        <span class="text-dashboard-muted">→</span>
        <input v-model="dateTo" type="date" class="px-3 py-1.5 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm" />
        <button @click="fetchActivity" class="px-3 py-1.5 bg-[#22282F] text-white rounded-lg text-sm font-medium hover:bg-[#22282F]/90">
          Appliquer
        </button>
        <button v-if="dateFrom || dateTo" @click="dateFrom=''; dateTo=''; fetchActivity()" class="text-xs text-dashboard-muted hover:text-dashboard-text">
          Réinitialiser
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="bg-white border border-dashboard-border rounded-xl p-16 text-center">
        <div class="w-10 h-10 border-4 border-[#22282F]/30 border-t-[#22282F] rounded-full animate-spin mx-auto mb-4" />
        <p class="text-dashboard-muted">Chargement…</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle class="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <p class="font-medium text-red-800">Erreur</p>
          <p class="text-sm text-red-600 mt-1">{{ error }}</p>
        </div>
      </div>

      <template v-else>
        <!-- Stats -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatCard label="Actions récentes" :value="String(recent.length)" :icon="Activity" tone="slate" />
          <StatCard label="Types d'actions" :value="String(stats.length)" :icon="ListChecks" tone="indigo" />
          <StatCard label="Critiques" :value="String(criticalCount)" :icon="ShieldAlert" tone="red" />
          <StatCard label="Adresses IP" :value="String(uniqueIps.size)" :icon="Globe2" tone="blue" />
        </div>

        <!-- Layout 2 cols : stats par action + chronologie -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <!-- Stats par action -->
          <div class="bg-white border border-dashboard-border rounded-xl shadow-sm lg:col-span-1">
            <div class="px-4 py-3 border-b border-dashboard-border flex items-center gap-2">
              <BarChart3 class="w-4 h-4 text-dashboard-muted" />
              <h2 class="text-sm font-semibold text-dashboard-text">Top actions</h2>
            </div>
            <div v-if="stats.length === 0" class="p-6 text-center text-sm text-dashboard-muted italic">
              Aucune action sur la période.
            </div>
            <ul v-else class="divide-y divide-dashboard-border max-h-[640px] overflow-y-auto">
              <li
                v-for="s in stats"
                :key="s.action"
                class="px-4 py-2.5 flex items-center justify-between gap-2 hover:bg-dashboard-bg"
              >
                <div class="flex items-center gap-2 min-w-0">
                  <component :is="getDomainMeta(s.action).icon" class="w-3.5 h-3.5 text-dashboard-muted flex-shrink-0" />
                  <span class="text-sm text-dashboard-text truncate">{{ getActionLabel(s.action) }}</span>
                </div>
                <span class="text-sm font-semibold text-dashboard-text bg-dashboard-bg px-2 py-0.5 rounded">{{ s.count }}</span>
              </li>
            </ul>
          </div>

          <!-- Chronologie -->
          <div class="bg-white border border-dashboard-border rounded-xl shadow-sm lg:col-span-2 overflow-hidden">
            <div class="px-4 py-3 border-b border-dashboard-border flex items-center gap-2">
              <History class="w-4 h-4 text-dashboard-muted" />
              <h2 class="text-sm font-semibold text-dashboard-text">Chronologie (1000 dernières)</h2>
            </div>
            <div v-if="recent.length === 0" class="p-12 text-center text-sm text-dashboard-muted italic">
              Aucune action sur la période.
            </div>
            <ul v-else class="divide-y divide-dashboard-border max-h-[640px] overflow-y-auto">
              <li
                v-for="entry in recent"
                :key="entry.id"
              >
                <button
                  @click="selectedLog = entry"
                  :class="[
                    'w-full text-left px-4 py-3 hover:bg-dashboard-bg transition-colors flex items-start gap-3 border-l-4',
                    SEVERITY_META[entry.severity].ring,
                  ]"
                >
                  <component
                    :is="getDomainMeta(entry.action).icon"
                    class="w-4 h-4 mt-0.5 text-dashboard-muted flex-shrink-0"
                  />
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                      <span class="font-medium text-dashboard-text text-sm">{{ getActionLabel(entry.action) }}</span>
                      <span v-if="entry.severity !== 'info'" :class="['text-[10px] px-1.5 py-0.5 rounded font-medium', SEVERITY_META[entry.severity].badge]">
                        {{ SEVERITY_META[entry.severity].label }}
                      </span>
                    </div>
                    <div class="text-xs text-dashboard-muted mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>{{ formatDateTime(entry.created_at) }}</span>
                      <template v-if="entry.courrier_id">
                        <span>·</span>
                        <router-link :to="`/audit/courrier/${entry.courrier_id}`" @click.stop class="text-blue-600 hover:underline">
                          Courrier #{{ entry.courrier_id }}
                        </router-link>
                      </template>
                      <template v-else-if="entry.target_id">
                        <span>·</span>
                        <span>{{ entry.target_type }} #{{ entry.target_id }}</span>
                      </template>
                      <template v-if="entry.ip_address">
                        <span>·</span>
                        <span class="font-mono">{{ entry.ip_address }}</span>
                      </template>
                    </div>
                  </div>
                  <ChevronRight class="w-4 h-4 text-dashboard-muted flex-shrink-0 mt-1" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      </template>
    </div>

    <AuditLogDetailPanel
      v-if="selectedLog"
      :log="selectedLog"
      @close="selectedLog = null"
    />
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  ArrowLeft, User, History, AlertCircle, Activity, ShieldAlert,
  ListChecks, Globe2, BarChart3, ChevronRight,
} from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import AuditLogDetailPanel from '@/components/audit/AuditLogDetailPanel.vue'
import StatCard from '@/components/audit/StatCard.vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'
import {
  SEVERITY_META, getActionLabel, getDomainMeta, formatDateTime,
} from '@/utils/auditMeta'
import type { AuditLogEntry } from '@/utils/auditMeta'

const route = useRoute()
const toast = useToast()

const userId = computed(() => Number(route.params.id))
const recent = ref<AuditLogEntry[]>([])
const stats = ref<Array<{ action: string; count: number }>>([])
const loading = ref(false)
const error = ref('')
const selectedLog = ref<AuditLogEntry | null>(null)

const dateFrom = ref('')
const dateTo = ref('')

const userName = computed(() => {
  const first = recent.value.find(r => r.user?.id === userId.value)?.user
  return first ? `${first.full_name} (@${first.username})` : ''
})

const criticalCount = computed(() => recent.value.filter(e => e.severity === 'critical').length)
const uniqueIps = computed(() => new Set(recent.value.filter(e => e.ip_address).map(e => e.ip_address!)))

async function fetchActivity() {
  loading.value = true
  error.value = ''
  try {
    const params = new URLSearchParams()
    if (dateFrom.value) params.set('from', `${dateFrom.value}T00:00:00`)
    if (dateTo.value) params.set('to', `${dateTo.value}T23:59:59`)
    const qs = params.toString()
    const result = await apiFetch(`/api/admin/audit/user/${userId.value}/${qs ? `?${qs}` : ''}`)
    recent.value = result.recent || []
    stats.value = result.stats_by_action || []
  } catch (e: any) {
    error.value = e?.message || 'Erreur'
    toast.error('Erreur', error.value)
  } finally {
    loading.value = false
  }
}

watch(userId, fetchActivity)
onMounted(fetchActivity)
</script>
